import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hash, Loader2, Lock, Search } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { browseChannels, joinChannel } from "@/lib/dmApi";
import { toastError } from "@/lib/toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Org-scoped Slack-style channel browser (replaces Org Explorer stub).
 */
export default function ChannelBrowser({ open, onOpenChange, orgId }) {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [joiningId, setJoiningId] = useState(null);

  useEffect(() => {
    if (!open || !accessToken || !orgId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await browseChannels(accessToken, orgId);
        if (!cancelled) setChannels(list);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message || err.message || "Failed to load channels"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, orgId]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setError(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const seen = new Set();
    const deduped = [];
    for (const c of channels) {
      const id = c.conversation_id;
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      deduped.push(c);
    }
    if (!q) return deduped;
    return deduped.filter(
      (c) =>
        (c.title || "").toLowerCase().includes(q) ||
        (c.team_name || "").toLowerCase().includes(q)
    );
  }, [channels, query]);

  const openChannel = async (ch) => {
    if (!orgId || !ch?.conversation_id) return;
    const path = `/o/${encodeURIComponent(orgId)}/messages/${ch.conversation_id}`;
    onOpenChange?.(false);
    if (ch.is_member) {
      navigate(path, { state: { channel: ch } });
      return;
    }
    // Organization-wide: preview + join. Restricted: locked empty preview (invite-only).
    navigate(path, {
      state: {
        channel: {
          ...ch,
          is_member: false,
          can_preview: ch.visibility === "public",
        },
      },
    });
  };

  const handleJoin = async (ch, e) => {
    e?.stopPropagation?.();
    if (!accessToken || !orgId || ch.visibility !== "public") return;
    setJoiningId(ch.conversation_id);
    try {
      const joined = await joinChannel(accessToken, orgId, ch.conversation_id);
      onOpenChange?.(false);
      navigate(
        `/o/${encodeURIComponent(orgId)}/messages/${joined.conversation_id}`,
        { state: { channel: joined } }
      );
    } catch (err) {
      toastError(err, "Could not join channel");
    } finally {
      setJoiningId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-2 border-b shrink-0">
          <DialogTitle>Channel Browser</DialogTitle>
          <DialogDescription>
            Discover and join channels in this workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 py-2 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search channels…"
              className="h-8 pl-8 text-sm"
            />
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
          {loading ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive p-4">{error}</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">
              {query.trim()
                ? `No matches for “${query.trim()}”.`
                : "No channels in this workspace yet."}
            </p>
          ) : (
            <ul className="divide-y">
              {filtered.map((ch) => {
                const isOrgWide = ch.visibility === "public";
                const orgWideOnRestrictedTeam =
                  isOrgWide && ch.team_visibility === "restricted";
                return (
                  <li key={ch.conversation_id}>
                    <button
                      type="button"
                      onClick={() => openChannel(ch)}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                    >
                      <div
                        className={cn(
                          "mt-0.5 flex h-8 w-8 items-center justify-center rounded-md shrink-0",
                          isOrgWide ? "bg-muted" : "bg-muted/80"
                        )}
                      >
                        {isOrgWide ? (
                          <Hash className="h-4 w-4" />
                        ) : (
                          <Lock className="h-4 w-4" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-sm truncate">
                            {ch.title}
                          </span>
                          {orgWideOnRestrictedTeam ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] h-5 border-amber-500/50 text-amber-700 dark:text-amber-400"
                            >
                              Organization-wide
                            </Badge>
                          ) : !isOrgWide ? (
                            <Badge variant="secondary" className="text-[10px] h-5">
                              Restricted
                            </Badge>
                          ) : null}
                          {ch.is_member && (
                            <Badge variant="secondary" className="text-[10px] h-5">
                              Joined
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {ch.team_name || "Team"}
                          {ch.member_count != null
                            ? ` · ${ch.member_count} members`
                            : ""}
                        </p>
                        {orgWideOnRestrictedTeam && (
                          <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                            Visible to everyone in the org
                          </p>
                        )}
                      </div>
                      {isOrgWide && !ch.is_member && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs shrink-0"
                          disabled={joiningId === ch.conversation_id}
                          onClick={(e) => handleJoin(ch, e)}
                        >
                          {joiningId === ch.conversation_id ? "…" : "Join"}
                        </Button>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
